import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring, useReducedMotion, useMotionTemplate } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import DataCanvas from "@/components/site/DataCanvas";
import { MaskedLines, EASE } from "@/lib/motion";
import { useDemo } from "@/components/ui/demo-provider";
import HeroWorkspace from "@/components/product/HeroWorkspace";

export default function Hero() {
  const demo = useDemo();
  const heroRef = useRef(null);
  const reduced = useReducedMotion();
  const [finePointer, setFinePointer] = useState(false);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);

  useEffect(() => {
    const mq = window.matchMedia("(pointer: fine)");
    const sync = () => setFinePointer(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  // Soft fade only - keeps continuity into the Platform section without a hard cut
  const copyOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0.35]);
  const copyY = useTransform(scrollYProgress, [0, 0.75], [0, -40]);
  const stageScale = useTransform(scrollYProgress, [0, 1], [1, 0.96]);
  const stageOpacity = useTransform(scrollYProgress, [0, 0.9], [1, 0.25]);
  const stageY = useTransform(scrollYProgress, [0, 1], [0, 48]);
  const scrollTilt = useTransform(scrollYProgress, [0, 0.55], [5, 0]);

  const rx = useSpring(useTransform(my, [-0.5, 0.5], [2.2, -2.2]), { stiffness: 55, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-2.8, 2.8]), { stiffness: 55, damping: 18 });

  const gx = useTransform(mx, [-0.5, 0.5], ["22%", "78%"]);
  const gy = useTransform(my, [-0.5, 0.5], ["22%", "78%"]);
  const glare = useMotionTemplate`radial-gradient(480px circle at ${gx} ${gy}, rgba(0,245,212,0.05), transparent 62%)`;

  const onMouseMove = (e) => {
    if (reduced || !finePointer) return;
    const r = heroRef.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      ref={heroRef}
      onMouseMove={onMouseMove}
      className="relative flex min-h-screen items-center"
      data-testid="hero-section"
    >
      {/* Canvas covers the full hero and bleeds softly into Platform — fade only after the section edge */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 2, ease: "easeOut" }}
        className="pointer-events-none absolute inset-x-0 top-0 bottom-[-22vh]"
        style={{
          WebkitMaskImage: "linear-gradient(to bottom, #000 0%, #000 90%, rgba(0,0,0,0.4) 96%, transparent 100%)",
          maskImage: "linear-gradient(to bottom, #000 0%, #000 90%, rgba(0,0,0,0.4) 96%, transparent 100%)",
        }}
      >
        <DataCanvas className="h-full w-full" />
      </motion.div>
      <div className="grid-bg absolute inset-0 opacity-40 [mask-image:radial-gradient(ellipse_70%_60%_at_50%_40%,black,transparent)]" />
      <div className="noise-overlay absolute inset-0 opacity-[0.035]" />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-12 px-5 pb-24 pt-36 sm:px-8 lg:grid-cols-12 lg:gap-8 lg:pt-28">
        <motion.div style={{ opacity: copyOpacity, y: copyY }} className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: -14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.25 }}
            className="mb-7 inline-flex items-center gap-3 rounded-full border border-white/[0.08] bg-white/[0.03] py-1.5 pl-2 pr-4 backdrop-blur-md"
            data-testid="hero-eyebrow"
          >
            <span className="rounded-full bg-teal/15 px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-teal">
              RORAQ
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-slate-400">
              The operational layer around payroll
            </span>
          </motion.div>

          <h1
            className="font-display text-[13.5vw] font-semibold leading-[0.98] tracking-tight text-slate-50 sm:text-6xl lg:text-[4.6rem]"
            data-testid="hero-headline"
          >
            <MaskedLines
              immediate
              delay={0.45}
              stagger={0.16}
              lines={[
                <>Turn payroll data</>,
                <>
                  into <span className="text-glow-teal text-teal">action.</span>
                </>,
              ]}
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.95 }}
            className="mt-7 max-w-md text-base leading-relaxed text-slate-300 md:text-lg"
            data-testid="hero-subcopy"
          >
            RORAQ brings reporting, compliance, workforce insights and payroll workflows into one connected
            workspace - while your payroll system remains the source of truth.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 1.15 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button type="button" onClick={demo.open} className="btn-primary" data-testid="hero-request-demo-button">
              Request a demo
              <ArrowRight className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => document.querySelector("#platform")?.scrollIntoView({ behavior: "smooth" })}
              className="btn-ghost"
              data-testid="hero-explore-button"
            >
              Explore the platform
              <ArrowDown className="h-4 w-4" />
            </button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.5 }}
            className="mt-8 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-600"
          >
            Payroll data goes in - RORAQ makes it operational
          </motion.p>
        </motion.div>

        <motion.div
          style={{
            scale: stageScale,
            opacity: stageOpacity,
            y: stageY,
            rotateX: finePointer && !reduced ? scrollTilt : 0,
            transformPerspective: 1400,
          }}
          className="relative lg:col-span-7"
          data-testid="hero-3d-stage"
        >
          <div className="relative w-full [perspective:1400px]">
            <motion.div
              className="relative w-full"
              style={{
                rotateX: finePointer && !reduced ? rx : 0,
                rotateY: finePointer && !reduced ? ry : 0,
                transformStyle: "preserve-3d",
              }}
            >
              <motion.div
                className="pointer-events-none absolute -left-6 top-10 hidden h-56 w-72 rounded-full bg-[color:var(--bg-secondary)]/55 blur-3xl lg:block"
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.8 }}
                transition={{ duration: 1.6, delay: 1.1 }}
                aria-hidden="true"
              />
              <motion.div
                className="pointer-events-none absolute -right-4 bottom-8 hidden h-48 w-64 rounded-full bg-cy/[0.06] blur-3xl lg:block"
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.85 }}
                transition={{ duration: 1.6, delay: 1.4 }}
                aria-hidden="true"
              />

              <motion.div
                initial={{ opacity: 0, y: 28 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.05, ease: EASE, delay: 0.85 }}
                className="relative z-10 mx-auto w-full max-w-xl scale-100 lg:mx-0 lg:max-w-none lg:scale-[1.06] lg:origin-left"
                data-testid="hero-mobile-card"
              >
                <HeroWorkspace />
              </motion.div>

              <motion.div
                className="pointer-events-none absolute inset-0 z-30 hidden lg:block"
                style={{ background: glare }}
                initial={{ opacity: 0 }}
                animate={{ opacity: finePointer && !reduced ? 1 : 0 }}
                transition={{ duration: 1, delay: 1.8 }}
                aria-hidden="true"
              />
            </motion.div>
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.4, duration: 1 }}
        style={{ opacity: copyOpacity }}
        className="absolute bottom-7 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-2 md:flex"
        data-testid="hero-scroll-cue"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-600">Scroll</span>
        <div className="h-10 w-px overflow-hidden bg-white/10">
          <motion.div
            className="h-4 w-px bg-teal"
            animate={reduced ? {} : { y: [0, 40] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
      </motion.div>
    </section>
  );
}
